import React, {useEffect, useRef, useState} from 'react';
import {RightOutline} from 'antd-mobile-icons';

const THUMB_SIZE = 52;
const TRACK_PADDING = 2;
/**
 * 滑过 90% 才算完成。阈值定这么高是因为司机多在车上单手操作、常戴手套，
 * 半程松手的概率不低；触发一次「开始作业」就会落库打点，误触没法撤销。
 */
const COMPLETE_RATIO = 0.9;

interface PickUpSlideToStartProps {
    /** 滑轨上的提示文案 */
    text: string;
    /** 提交中禁止再次滑动 */
    disabled?: boolean;
    onComplete: () => void;
    /**
     * 变化时把滑块归位。滑到位后拇指会**停在终点**（见 handleEnd），
     * 正常路径下页面随即跳走所以看不出来；「滑了但不放行」的路径（今日无任务态下
     * 发现调度刚排了新任务）必须显式归位，否则司机面对一个已经推到底的滑块无从再滑
     */
    resetSignal?: number;
}

/**
 * 滑动确认条。没有用现成组件：antd-mobile 没有滑动解锁类控件，
 * 引第三方库只为这一个交互不划算。
 */
const PickUpSlideToStart: React.FC<PickUpSlideToStartProps> = ({text, disabled = false, onComplete, resetSignal}) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const startXRef = useRef<number>(0);
    const maxOffsetRef = useRef<number>(0);
    const slidingRef = useRef<boolean>(false);
    // 位移同时存 ref 与 state：touchend 回调里读 state 拿到的是这一轮渲染的旧值，
    // 判定是否滑到位必须用 ref
    const offsetRef = useRef<number>(0);
    const [offset, setOffset] = useState<number>(0);
    const [sliding, setSliding] = useState<boolean>(false);

    const updateOffset = (value: number) => {
        offsetRef.current = value;
        setOffset(value);
    };

    useEffect(() => {
        offsetRef.current = 0;
        setOffset(0);
    }, [resetSignal]);

    const handleStart = (clientX: number) => {
        if (disabled) {
            return;
        }
        const track = trackRef.current;
        if (!track) {
            return;
        }
        maxOffsetRef.current = track.clientWidth - THUMB_SIZE - TRACK_PADDING * 2;
        startXRef.current = clientX;
        slidingRef.current = true;
        setSliding(true);
    };

    const handleMove = (clientX: number) => {
        if (!slidingRef.current) {
            return;
        }
        const next = Math.min(Math.max(clientX - startXRef.current, 0), maxOffsetRef.current);
        updateOffset(next);
    };

    const handleEnd = () => {
        if (!slidingRef.current) {
            return;
        }
        slidingRef.current = false;
        setSliding(false);
        if (maxOffsetRef.current > 0 && offsetRef.current >= maxOffsetRef.current * COMPLETE_RATIO) {
            updateOffset(maxOffsetRef.current);
            onComplete();
        } else {
            // 没滑到位就回弹，不触发任何写入
            updateOffset(0);
        }
    };

    return (
        <div
            ref={trackRef}
            className={`relative h-14 rounded-full overflow-hidden select-none ${disabled ? 'opacity-60' : ''}`}
            style={{background: 'linear-gradient(90deg, #A996FF 0%, #7C5CFF 100%)'}}
            onTouchStart={(e) => handleStart(e.touches[0].clientX)}
            onTouchMove={(e) => handleMove(e.touches[0].clientX)}
            onTouchEnd={handleEnd}
            onTouchCancel={handleEnd}
            onMouseDown={(e) => handleStart(e.clientX)}
            onMouseMove={(e) => handleMove(e.clientX)}
            onMouseUp={handleEnd}
            onMouseLeave={handleEnd}
        >
            <div className="absolute inset-0 flex items-center justify-center text-white text-base font-medium pointer-events-none">
                {text}
            </div>
            <div
                className="absolute flex items-center justify-center bg-white rounded-full shadow"
                style={{
                    width: THUMB_SIZE,
                    height: THUMB_SIZE,
                    top: TRACK_PADDING,
                    left: TRACK_PADDING,
                    transform: `translateX(${offset}px)`,
                    // 拖动中不加过渡，否则跟手会有延迟；松手回弹才需要动画
                    transition: sliding ? 'none' : 'transform 0.2s ease-out',
                }}
            >
                <RightOutline className="text-xl" style={{color: 'var(--adm-color-primary)'}}/>
            </div>
        </div>
    );
};

export default PickUpSlideToStart;
